import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "@/api";
import { useCart } from "@/context/CartContext";
import { payWithRazorpay } from "@/razorpay";
import { Reveal, Overline, Magnetic } from "@/components/Motion";

export default function Cart() {
  const { items, setQty, remove, clear, total } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [payUrl, setPayUrl] = useState(null);

  useEffect(() => {
    api.get("/config").then((r) => setPayUrl(r.data.payment_page_url)).catch(() => {});
  }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const checkout = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.qty })),
        customer: form,
      });
      const order = res.data;
      if (order.razorpay_order) {
        const cfg = (await api.get("/config")).data;
        await payWithRazorpay({
          order: order.razorpay_order,
          keyId: cfg.razorpay_key_id,
          prefill: { name: form.name, email: form.email, contact: form.phone },
          description: "IKSHA Shop order",
          onSuccess: async (resp) => {
            await api.post(`/orders/${order.id}/verify`, resp);
            toast.success("Payment received — order confirmed.");
            clear();
            setOrderResult({ id: order.id, total: order.total });
          },
          onDismiss: () => toast.error("Payment incomplete — your order is saved as pending."),
        });
      } else {
        toast.success("Order placed — IKSHA will contact you to arrange payment & delivery.");
        clear();
        setOrderResult({ id: order.id, total: order.total });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (orderResult) {
    return (
      <main data-testid="order-success" className="pt-40 pb-24 px-6 md:px-12 max-w-3xl mx-auto min-h-screen text-center">
        <span className="w-3 h-3 rounded-full bg-brand inline-block" />
        <h1 className="font-display text-4xl md:text-6xl tracking-tight mt-6">Order placed.</h1>
        <p className="mt-4 text-ink/60">Reference <span className="font-mono text-sm">{orderResult.id}</span></p>
        {payUrl && orderResult.total > 0 && (
          <>
            <a href={payUrl} target="_blank" rel="noreferrer" data-testid="pay-now-btn"
              className="mt-8 inline-block bg-brand text-paper px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand-hover transition-colors">
              Pay {inr(orderResult.total)} via Razorpay →
            </a>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40 max-w-sm mx-auto leading-relaxed">
              Enter the amount above on the payment page and mention order reference {orderResult.id} in the note
            </p>
          </>
        )}
        <div>
          <Link to="/shop" data-testid="continue-shopping-btn" className="mt-10 inline-block bg-ink text-paper px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand transition-colors">
            Continue Browsing
          </Link>
        </div>
      </main>
    );
  }

  const inputCls = "w-full bg-transparent border-b border-ink/25 focus:border-brand outline-none py-3 placeholder:text-ink/30 transition-colors";

  return (
    <main data-testid="cart-page" className="pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto min-h-screen">
      <Overline>Cart</Overline>
      <h1 className="font-display text-5xl sm:text-6xl tracking-tight mt-6 mb-14">Your selection</h1>
      {!items.length ? (
        <Reveal>
          <p className="text-ink/60 mb-8">Your cart is empty — the shop is full of small, deliberate things.</p>
          <Link to="/shop" data-testid="empty-cart-shop-link" className="link-line font-mono text-xs uppercase tracking-[0.2em]">Visit the Shop</Link>
        </Reveal>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7 space-y-8">
            {items.map(({ product: p, qty }) => (
              <Reveal key={p.id}>
                <div data-testid={`cart-item-${p.id}`} className="flex gap-6 border-b border-ink/15 pb-8">
                  <div className="w-24 h-32 shrink-0 frame-clip bg-linen border border-ink/10">
                    <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between gap-4">
                      <h2 className="font-display text-xl">{p.name}</h2>
                      <button data-testid={`remove-${p.id}`} onClick={() => remove(p.id)} className="text-ink/40 hover:text-brand transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40 mt-1">{p.category}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center border border-ink/25">
                        <button data-testid={`cart-minus-${p.id}`} onClick={() => setQty(p.id, qty - 1)} className="p-2 hover:text-brand transition-colors"><Minus size={12} /></button>
                        <span className="font-mono text-xs w-8 text-center">{qty}</span>
                        <button data-testid={`cart-plus-${p.id}`} onClick={() => setQty(p.id, Math.min(p.quantity || 99, qty + 1))} className="p-2 hover:text-brand transition-colors"><Plus size={12} /></button>
                      </div>
                      <span className="font-mono text-sm">{inr(p.price * qty)}</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="bg-ink text-paper p-8 md:p-10">
              <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-brand">Checkout</h2>
              <p className="font-display text-4xl mt-4">{inr(total)}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/40 mt-1">Shipping calculated on confirmation</p>
              <form onSubmit={checkout} data-testid="checkout-form" className="mt-8 space-y-5">
                <input required data-testid="checkout-name" value={form.name} onChange={set("name")} placeholder="Full name"
                  className="w-full bg-transparent border-b border-paper/25 focus:border-brand outline-none py-3 placeholder:text-paper/30 transition-colors" />
                <input required type="email" data-testid="checkout-email" value={form.email} onChange={set("email")} placeholder="Email"
                  className="w-full bg-transparent border-b border-paper/25 focus:border-brand outline-none py-3 placeholder:text-paper/30 transition-colors" />
                <input required data-testid="checkout-phone" value={form.phone} onChange={set("phone")} placeholder="Phone"
                  className="w-full bg-transparent border-b border-paper/25 focus:border-brand outline-none py-3 placeholder:text-paper/30 transition-colors" />
                <textarea rows={2} data-testid="checkout-address" value={form.address} onChange={set("address")} placeholder="Delivery address"
                  className="w-full bg-transparent border-b border-paper/25 focus:border-brand outline-none py-3 placeholder:text-paper/30 transition-colors resize-none" />
                <Magnetic>
                  <button type="submit" disabled={busy} data-testid="checkout-pay-btn"
                    className="w-full bg-brand text-paper py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand-hover transition-colors disabled:opacity-50">
                    {busy ? "Processing…" : `Place Order — ${inr(total)}`}
                  </button>
                </Magnetic>
              </form>
            </div>
          </Reveal>
        </div>
      )}
    </main>
  );
}
